import { useToast } from "@/components/toast-provider";
import { Routes } from "@/constants/routes";
import { useCategories } from "@/hooks/categories/useCategories";
import { useProducts } from "@/hooks/products/useProducts";
import { Loading } from "@uigovpe/components";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductActionDialog from "./(components)/product-action-dialog";
import ProductFilters from "./(components)/product-filters";
import ProductSummary from "./(components)/product-summary";
import ProductTable from "./(components)/product-table";
import { mapMetrics, mapProduct } from "./mappers";
import type { PendingProductAction, ProductStatusFilter } from "./types";

export default function MyProducts() {
  const { categoryOptions: categories, loadCategories } = useCategories();
  const { changeProductStatus, isLoading, isMutating: isActing, loadProducts, productsData: data, removeProduct } = useProducts();
  const [first, setFirst] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState<ProductStatusFilter>("all");
  const [pendingAction, setPendingAction] =
    useState<PendingProductAction | null>(null);
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.allSettled([loadProducts(), loadCategories()]).then((results) => {
      results.forEach((result) => {
        if (result.status === "rejected") showErrorToast(result.reason instanceof Error ? result.reason.message : "Não foi possível carregar os dados.");
      });
    });
  }, [loadCategories, loadProducts, showErrorToast]);

  const products = useMemo(
    () => data.produtos.map(mapProduct),
    [data.produtos],
  );
  const metrics = useMemo(() => mapMetrics(data), [data]);
  const filteredProducts = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    return products.filter(
      (product) =>
        (status === "all" || product.status === status) &&
        (category === "all" || product.categoryId === category) &&
        (!term ||
          product.name.toLocaleLowerCase("pt-BR").includes(term) ||
          product.keyword.toLocaleLowerCase("pt-BR").includes(term)),
    );
  }, [category, products, search, status]);

  const resetPage = () => setFirst(0);
  const confirmAction = async () => {
    if (!pendingAction) return;
    try {
      if (pendingAction.type === "delete")
        await removeProduct(pendingAction.product.id);
      else
        await changeProductStatus(
          pendingAction.product.id,
          pendingAction.type === "publish" ? "PUBLICADO" : "OCULTO",
        );
      showSuccessToast(
        {
          delete: "Produto apagado com sucesso.",
          hide: "Produto ocultado com sucesso.",
          publish: "Produto publicado com sucesso.",
        }[pendingAction.type],
      );
      setPendingAction(null);
      await loadProducts();
    } catch (error) {
      showErrorToast(
        error instanceof Error
          ? error.message
          : "Não foi possível atualizar o produto.",
      );
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex! min-h-[320px]! w-full! items-center! justify-center!"
        role="status"
        aria-live="polite"
        aria-label="Carregando produtos"
      >
        <Loading />
        <span className="sr-only">Carregando produtos...</span>
      </div>
    );
  }

  return (
    <main className="min-h-0! w-full! min-w-0! p-5!">
      <ProductActionDialog
        action={pendingAction}
        isLoading={isActing}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmAction}
      />
      <div className="flex! w-full! min-w-0! flex-col! gap-10!">
        <ProductSummary {...metrics} />
        <section className="flex! w-full! min-w-0! flex-col! gap-8!">
            <ProductFilters
              categories={categories}
              category={category}
              search={search}
              status={status}
              onCategoryChange={(value) => {
                setCategory(value);
                resetPage();
              }}
              onNewProduct={() => navigate(Routes.ADD_PRODUCTS)}
              onSearchChange={(value) => {
                setSearch(value);
                resetPage();
              }}
              onStatusChange={(value) => {
                setStatus(value);
                resetPage();
              }}
            />
            <ProductTable
              first={first}
              isLoading={isLoading}
              products={filteredProducts}
              onAction={setPendingAction}
              onPageChange={setFirst}
            />
        </section>
      </div>
    </main>
  );
}
